import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

import type { AssistantQuestion } from '../types';
import { useAssistant } from '../AssistantProvider';

/*
 * Structured question.
 * - single-select (default): full-width option buttons; clicking one posts
 *   immediately and echoes the choice as a user bubble.
 * - multi_select: checkboxes + a Submit button.
 * - allow_free_text: also show a free-text box (with Submit).
 * Once `answered_by` is set (or a newer message supersedes it), controls are
 * disabled and the selected option(s) keep the navy/tinted style.
 */
const QuestionBlock = ({ question }: { question: AssistantQuestion }) => {
  const { t } = useTranslation('assistant');
  const { answerQuestion } = useAssistant();
  const [checked, setChecked] = useState<string[]>([]);
  const [freeText, setFreeText] = useState('');

  const answered = Boolean(question.answered_by);
  const selected = question.selected_options || [];
  const isSelected = (value: string) => selected.includes(value);

  const prompt = question.text ? (
    <div className="bra-qtext">{question.text}</div>
  ) : null;

  const freeTextBox =
    question.allow_free_text && !answered ? (
      <textarea
        className="bra-freetext"
        placeholder={t('Type your answer…')}
        value={freeText}
        onChange={(e) => setFreeText(e.target.value)}
      />
    ) : null;

  // ---- single-select ----
  if (!question.multi_select) {
    const submitFreeText = () => {
      const text = freeText.trim();
      if (text) answerQuestion(question, [text], text);
    };
    return (
      <div className="bra-q">
        {prompt}
        {question.options.map((o) => (
          <button
            key={o.value}
            type="button"
            className={`bra-opt${isSelected(o.value) ? ' bra-sel' : ''}`}
            disabled={answered}
            onClick={() => answerQuestion(question, [o.value], o.label)}
          >
            <span>{o.label}</span>
            {o.meta ? <span className="bra-meta">{o.meta}</span> : null}
          </button>
        ))}
        {freeTextBox}
        {question.allow_free_text && !answered ? (
          <button
            type="button"
            className="bra-btn bra-pri"
            disabled={!freeText.trim()}
            onClick={submitFreeText}
          >
            {t('Submit')}
          </button>
        ) : null}
      </div>
    );
  }

  // ---- multi-select ----
  const toggle = (value: string) =>
    setChecked((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );

  const submit = () => {
    const values = [...checked];
    const labels = question.options
      .filter((o) => checked.includes(o.value))
      .map((o) => o.label);
    if (question.allow_free_text && freeText.trim()) {
      values.push(freeText.trim());
      labels.push(freeText.trim());
    }
    if (!values.length) return;
    answerQuestion(question, values, labels.join(', '));
  };

  const canSubmit =
    checked.length > 0 ||
    (question.allow_free_text && freeText.trim().length > 0);

  return (
    <div className="bra-q">
      {prompt}
      {question.options.map((o) => {
        const on = answered ? isSelected(o.value) : checked.includes(o.value);
        return (
          <button
            key={o.value}
            type="button"
            className={`bra-opt${on ? ' bra-sel' : ''}`}
            disabled={answered}
            onClick={() => toggle(o.value)}
          >
            <input
              type="checkbox"
              className="bra-opt-check"
              readOnly
              checked={on}
              tabIndex={-1}
            />
            <span>{o.label}</span>
            {o.meta ? <span className="bra-meta">{o.meta}</span> : null}
          </button>
        );
      })}
      {freeTextBox}
      {!answered ? (
        <button
          type="button"
          className="bra-btn bra-pri"
          disabled={!canSubmit}
          onClick={submit}
        >
          {t('Submit')}
        </button>
      ) : null}
    </div>
  );
};

export default QuestionBlock;
